import { Card, Typography, Box, Alert, Button } from '@mui/material';
import { useStoreActions } from 'actions/store-actions';
import { useUserActions } from 'actions/user-actions';
import { IPageData } from 'common/interfaces';
import { IStore, IStoreUser } from 'common/interfaces/istore';
import UILogger from 'common/utils/logger';
import AlertDialog from 'components/lib/alert-dialog/AlertDialog';
import ImageBox from 'components/lib/image-box';
import StoreSelectionView from 'components/store/store-selection/StoreSelection';
import React, { CSSProperties, FC, useEffect, useState } from 'react';
import { useAppSelector } from 'setup/store';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import UICard from 'components/lib/card/UICard';
import { IRole } from 'common/interfaces/irole';
import { UserRoleTypeEnum } from 'common/constants/user';

type UserStoresUpdateProps = {
  userId: number;
  franchiseeId: number;
  roleId: number;
  styles?: CSSProperties;
  onStoresUpdated: (storeIds: number[]) => void;
};

const BG_COLORS = [
  '#8dd3c7',
  '#ffffb3',
  '#bebada',
  '#fb8072',
  '#80b1d3',
  '#fdb462',
  '#b3de69',
  '#fccde5',
  '#d9d9d9',
  '#bc80bd',
] as string[];

const UserStoresUpdate: FC<UserStoresUpdateProps> = ({ userId, franchiseeId, roleId, onStoresUpdated, styles }) => {
  const loggedInUser = useAppSelector((state) => state.appReducer.loggedInUser);
  const roleIdMap = useAppSelector((state) => state.userReducer.roleIdMap);

  const [_userStores, setUserStores] = useState<IPageData<IStore>>();

  const [showStoreSelection, setStoreSelection] = useState<boolean>(false);
  const [resetAddStore, setResetAddStore] = useState<boolean>(false);
  const [canEditStoreMapping, setCanEditStoreMapping] = useState<boolean>(false);
  const [showStores, setShowStores] = useState<boolean>(false);
  const { loadAllRoles, mapStoresToUser, removeUserStore } = useUserActions();
  const { loadStoresForUser } = useStoreActions();
  const { t } = useTranslation();
  const [storeToDelete, setStoreToDelete] = useState<IStore>();

  const _loadUserStores = async (userId: number) => {
    const stores = await loadStoresForUser(userId, 1, 100);
    setUserStores(stores);
    setResetAddStore(true);
    return stores;
  };

  useEffect(() => {
    console.log('Mounted storeScreen.....1', userId);
    if (userId) {
      _loadUserStores(userId);
      loadAllRoles();
    }
  }, [userId]);

  useEffect(() => {
    if (roleId && roleIdMap && loggedInUser) {
      setShowStores(roleIdMap && roleIdMap[roleId].roleName != UserRoleTypeEnum.MASTER_ADMIN);
      setCanEditStoreMapping(
        roleIdMap &&
          roleIdMap[roleId].roleName != UserRoleTypeEnum.MASTER_ADMIN &&
          roleIdMap[roleId].roleName != UserRoleTypeEnum.FRANCHISEE_ADMIN,
      );
    }
  }, [roleIdMap, userId, loggedInUser]);

  const _confirmRemoveStore = async () => {
    if (userId && storeToDelete?.id) {
      const resp = await removeUserStore(userId, storeToDelete.id);
      if (resp.status) {
        const data = await _loadUserStores(userId);
        onStoresUpdated && onStoresUpdated(data ? data.items.map((ds) => ds.id) : []);
        _cancelRemoveStore();
      }
    }
  };

  const _cancelRemoveStore = async () => {
    setStoreToDelete(undefined);
  };

  const _handleRemoveStore = async (store: IStore) => {
    UILogger.log('_handleRemoveStore:', store);
    if (userId && store?.id) {
      setStoreToDelete(store);
    }
  };

  const _handleAddStores = async (stores: IStore[] | undefined) => {
    if (userId && stores && stores.length > 0) {
      const storeIds = stores.map((store) => store.id);
      setResetAddStore(false);
      setStoreSelection(false);
      const resp = await mapStoresToUser(userId, { userId, storeIds } as IStoreUser);
      if (resp && resp.status) {
        const data = await _loadUserStores(userId);
        onStoresUpdated && onStoresUpdated(data ? data.items.map((ds) => ds.id) : []);
      }
    }
  };

  const _onPressAddStore = () => {
    setStoreSelection(!showStoreSelection);
  };
  return (
    <React.Fragment>
      {showStores ? (
        // && [roleId].roleName != UserRoleTypeEnum.FRANCHISEE_ADMIN
        <UICard title="Stores" styles={styles}>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} flexWrap={'wrap'}>
            {_userStores && _userStores.totalItems > 0 ? (
              _userStores.items.map((store, index) => (
                <ImageBox
                  key={store.id.toString()}
                  title={'#' + store.storeNumber}
                  info={store.storeName}
                  info2={store.zipcode || ''}
                  data={store}
                  onDeleteClick={canEditStoreMapping ? _handleRemoveStore : undefined}
                  deteBtnText="Remove"
                  styles={{
                    backgroundColor: BG_COLORS[index % BG_COLORS.length],
                    color: 'black',
                  }}
                />
              ))
            ) : (
              <Alert severity="warning">No stores associated with this user. </Alert>
            )}
          </Box>
          {canEditStoreMapping ? (
            <div style={{ margin: '40px 0px 10px', display: 'flex', alignItems: 'center' }}>
              {!showStoreSelection ? (
                <Button variant="contained" onClick={_onPressAddStore} style={{ height: '50px', marginLeft: '0px' }}>
                  <AddCircleIcon /> &nbsp;Add New Store
                </Button>
              ) : null}
              {showStoreSelection ? (
                <StoreSelectionView
                  onSubmit={_handleAddStores}
                  reset={resetAddStore}
                  franchiseeId={franchiseeId}
                  selectedStores={[]}
                  // selectedStores={_userStores?.items}
                />
              ) : null}
            </div>
          ) : null}
        </UICard>
      ) : null}
      {storeToDelete ? (
        <AlertDialog
          title="Remove Store"
          message={'Do you really want to remove the store #' + storeToDelete?.storeNumber + ' from this user?'}
          onConfirmClick={_confirmRemoveStore}
          onCancelClick={_cancelRemoveStore}
          cancelBtnText="Cancel"
          confirmBtnText="Confirm"
          showAlert
        />
      ) : null}
    </React.Fragment>
  );
};

export default UserStoresUpdate;
